/**
 * Shared plumbing for visualize client tools that mutate the current
 * workspace through PubSubBus panel-mutation actions.
 *
 * The bus invokes subscribers synchronously in the publisher's stack (a
 * documented contract on {@link AiAgentThreadPubSubBus.publish}), so the
 * workspace's result callback has already fired by the time `publish()`
 * returns — {@link runPanelMutation} exploits that to give tools
 * request/response semantics over a fire-and-forget bus.
 */
import type { TopicService } from "@/shared/domain/topics/TopicService";
import type {
  PanelMutationActionPayload,
  WorkspaceActionEntry,
  WorkspaceActionResult,
} from "@/shared/services/AiAgentThreadPubSubBus/models";

import type { ClientToolExecutionResult } from "../ClientTools";
import { toolError, toolSuccess } from "../ClientTools";

/**
 * Upper bound on message paths per tool call. Keeps a single call from
 * flooding one panel (and the per-id record fetches) while comfortably
 * fitting real groupings like a 6-axis IMU.
 */
export const MAX_MESSAGE_PATHS_PER_CALL = 20;

/** True when `value` is an array of strings (the shape of id-list inputs). */
export function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

export interface FetchedEntries {
  entries: WorkspaceActionEntry[];
  /**
   * Ids whose records could not be loaded, with per-id reasons in the same
   * shape tools surface as `invalid_paths`. A stale or mistyped id is the
   * most common model mistake, so every failing id is reported — not just
   * the first.
   */
  invalid: { message_path_id: string; reason: string }[];
}

function reasonFromRejection(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}

/**
 * Fetch the message path + topic record for every id, deduping topic fetches
 * across paths that share a topic. Never throws for per-id failures; they
 * come back in `invalid` so the caller can fail atomically with per-path
 * reasons.
 */
export async function fetchEntries(
  topicService: TopicService,
  messagePathIds: string[],
): Promise<FetchedEntries> {
  const invalid: FetchedEntries["invalid"] = [];

  const messagePathResults = await Promise.allSettled(
    messagePathIds.map((id) => topicService.getMessagePathById(id)),
  );
  const messagePaths = messagePathResults.flatMap((result, i) => {
    if (result.status === "fulfilled") {
      return [result.value];
    }
    invalid.push({
      message_path_id: messagePathIds[i],
      reason: reasonFromRejection(result.reason),
    });
    return [];
  });

  const topicIds = [...new Set(messagePaths.map((mp) => mp.topic_id))];
  const topicResults = await Promise.allSettled(
    topicIds.map((id) => topicService.getTopicById(id)),
  );
  const topicsById = new Map(
    topicResults.flatMap((result) =>
      result.status === "fulfilled"
        ? [[result.value.topic_id, result.value] as const]
        : [],
    ),
  );
  const topicFailures = new Map(
    topicResults.flatMap((result, i) =>
      result.status === "rejected"
        ? [[topicIds[i], reasonFromRejection(result.reason)] as const]
        : [],
    ),
  );

  const entries: WorkspaceActionEntry[] = [];
  for (const messagePath of messagePaths) {
    const topic = topicsById.get(messagePath.topic_id);
    if (topic) {
      entries.push({ messagePath, topic });
    } else {
      invalid.push({
        message_path_id: messagePath.message_path_id,
        reason: `Failed to load its topic: ${
          topicFailures.get(messagePath.topic_id) ?? "topic not found"
        }`,
      });
    }
  }

  return { entries, invalid };
}

/**
 * Publish a panel-mutation workspace action and translate its synchronous
 * result into a client tool result.
 *
 * @param action - Action name echoed in the success response.
 * @param successExtra - Extra fields merged into the success response.
 */
export function runPanelMutation(
  publishWorkspaceAction: (payload: PanelMutationActionPayload) => boolean,
  action: string,
  buildPayload: (
    onResult: (result: WorkspaceActionResult) => void,
  ) => PanelMutationActionPayload,
  successExtra: Record<string, unknown> = {},
): ClientToolExecutionResult {
  let result: WorkspaceActionResult | undefined;

  const published = publishWorkspaceAction(
    buildPayload((r) => {
      result = r;
    }),
  );

  if (!published) {
    return toolError(
      "The visualizer is not ready to receive workspace actions yet. " +
        "Wait for the visualizer page to finish loading, then try again.",
    );
  }
  if (result === undefined) {
    return toolError("The visualizer did not report a result for this action.");
  }
  if (!result.ok) {
    return toolError(
      result.error,
      result.invalidPaths
        ? {
            invalid_paths: result.invalidPaths.map((path) => ({
              message_path_id: path.messagePathId,
              reason: path.reason,
            })),
          }
        : {},
    );
  }

  return toolSuccess(action, { panel_id: result.panelId, ...successExtra });
}
